import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { compareDocuments } from '../../lib/compareEngine';
import ChangeList from './ChangeList';
import ComparePanel from './ComparePanel';
import PdfPasswordModal from '../PdfPasswordModal';
import CompareExportDialog from './CompareExportDialog';
import { exportDiffPdf } from '../../lib/compareExport';
import { computeVisualBboxes } from '../../lib/compareWorkerCore';
import { useFileDialog } from '../../hooks/useFileDialog';

export default function CompareView({ file1, file2, onClose }) {
  const { t } = useTranslation();
  const [session, setSession] = useState(null);
  const [pageCount1, setPageCount1] = useState(0);
  const [pageCount2, setPageCount2] = useState(0);
  const [changes, setChanges] = useState([]);
  const [activeChangeId, setActiveChangeId] = useState(null);
  const [syncScroll, setSyncScroll] = useState(() => {
    return localStorage.getItem('scrivar_compare_sync_scroll') !== '0';
  });
  const [passwordPrompt, setPasswordPrompt] = useState(null); // { side, attemptsRemaining }
  const passwordAttemptsRef = useRef({}); // { left: count, right: count }
  const [imageOnly, setImageOnly] = useState(false);
  const [compareState, setCompareState] = useState('loading'); // 'loading' | 'complete' | 'error'
  const [errorMessage, setErrorMessage] = useState(null);

  const [exportOpen, setExportOpen] = useState(false);
  const { saveFile } = useFileDialog();

  async function handleExport({ filename, includeSummary }) {
    const file2Bytes = await file2.arrayBuffer();
    const bytes = await exportDiffPdf({
      file2Bytes,
      changes,
      includeSummary,
      file1Name: file1.name,
      file2Name: file2.name,
    });
    await saveFile(bytes, filename);
    setExportOpen(false);
  }

  // Persist syncScroll
  useEffect(() => {
    localStorage.setItem('scrivar_compare_sync_scroll', syncScroll ? '1' : '0');
  }, [syncScroll]);

  // Spin up the session when files change
  useEffect(() => {
    let cancelled = false;
    textObservedRef.current = false;
    setCompareState('loading');
    setErrorMessage(null);
    setChanges([]);
    setPageCount1(0);
    setPageCount2(0);
    // Reset visual-diff state so a new comparison doesn't inherit stale canvases.
    leftCanvasesRef.current = new Map();
    rightCanvasesRef.current = new Map();
    requestedVisualPagesRef.current = new Set();
    setVisualBboxesByPage({});
    compareDocuments({ file1, file2 }).then((s) => {
      if (cancelled) { s.terminate(); return; }
      setSession(s);
      s.on('loaded', ({ pageCount1, pageCount2 }) => {
        setPageCount1(pageCount1);
        setPageCount2(pageCount2);
      });
      s.on('page-text', ({ changes: pageChanges, hasText }) => {
        if (hasText) textObservedRef.current = true;
        setChanges((prev) => [...prev, ...pageChanges]);
      });
    });
    return () => {
      cancelled = true;
      session?.terminate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file1, file2]);

  // Detect image-only PDFs + flip compareState to 'complete' on text-complete.
  useEffect(() => {
    if (!session) return;
    return session.on('text-complete', () => {
      setCompareState((prev) => prev === 'error' ? prev : 'complete');
      // Image-only signal: no text items were observed on ANY page across
      // both files. If text existed but produced zero diffs (two identical
      // text PDFs), we don't suggest OCR — they're already comparable.
      if (!textObservedRef.current && (pageCount1 > 0 || pageCount2 > 0)) {
        setImageOnly(true);
      }
    });
  }, [session, pageCount1, pageCount2]);

  // Password-required + error listeners for encrypted PDFs.
  useEffect(() => {
    if (!session) return;
    passwordAttemptsRef.current = {}; // reset attempts on new session
    const off1 = session.on('password-required', ({ side }) => {
      const used = passwordAttemptsRef.current[side] || 0;
      setPasswordPrompt({ side, attemptsRemaining: Math.max(0, 3 - used) });
    });
    const off2 = session.on('error', (e) => {
      // Password-specific errors close the modal; the user can retry by
      // re-opening the file. All other errors flip the whole compare view
      // into the error state so the user actually sees what went wrong
      // instead of staring at a misleading "files are identical" empty state.
      if (e.message?.includes('Could not unlock')) {
        setPasswordPrompt(null);
        setCompareState('error');
        setErrorMessage(e.message);
      } else {
        setCompareState('error');
        setErrorMessage(e.message || 'Compare failed.');
      }
    });
    return () => { off1?.(); off2?.(); };
  }, [session]);

  function gotoChange(direction) {
    if (changes.length === 0) return;
    const idx = changes.findIndex((c) => c.id === activeChangeId);
    const nextIdx = direction === 'next'
      ? (idx + 1) % changes.length
      : (idx - 1 + changes.length) % changes.length;
    setActiveChangeId(changes[nextIdx].id);
  }

  const [scrollTargetLeft, setScrollTargetLeft] = useState(null);
  const [scrollTargetRight, setScrollTargetRight] = useState(null);
  const lastScrollSourceRef = useRef(null);

  const [visualBboxesByPage, setVisualBboxesByPage] = useState({}); // {pageIndex: bboxes[]}
  const requestedVisualPagesRef = useRef(new Set());
  const textObservedRef = useRef(false);

  // Main-thread visual diff: ComparePanel surfaces each rendered canvas via
  // `onCanvasReady`. We accumulate them per side, and when both sides have
  // produced a canvas for the same page AND that page has been requested
  // (i.e. is visible per the IntersectionObserver), run pixelmatch on the
  // pair right here. This avoids pdfjs's `page.render()` in the Compare
  // worker, which fails on pages with inline images because pdfjs reaches
  // into `document.createElement` for image scaling.
  const leftCanvasesRef = useRef(new Map()); // page → HTMLCanvasElement
  const rightCanvasesRef = useRef(new Map());

  function maybeComputeVisualDiff(page) {
    if (!requestedVisualPagesRef.current.has(page)) return;
    const left = leftCanvasesRef.current.get(page);
    const right = rightCanvasesRef.current.get(page);
    if (!left || !right) return;
    if (visualBboxesByPage[page]) return; // already computed for this page

    const W = Math.max(left.width, right.width);
    const H = Math.max(left.height, right.height);

    const leftCtx = left.getContext('2d');
    const rightCtx = right.getContext('2d');
    const rgbaA = padRgba(leftCtx.getImageData(0, 0, left.width, left.height), W, H);
    const rgbaB = padRgba(rightCtx.getImageData(0, 0, right.width, right.height), W, H);

    let bboxes;
    try {
      bboxes = computeVisualBboxes(rgbaA, rgbaB, W, H);
    } catch (err) {
      // Defensive — pixelmatch can throw on size mismatch in pathological cases.
      // eslint-disable-next-line no-console
      console.warn('[CompareView] visual diff failed for page', page, err);
      bboxes = [];
    }

    // Canvases now render at devicePixelRatio for crisp HiDPI output, so
    // bbox coords are in render-pixel space. Badges are positioned as CSS
    // pixels (the canvas's `style.width` matches the PDF's native point
    // dimensions). Divide by outputScale to land at the correct CSS position.
    const outputScale = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    const adjusted = bboxes.map((b, i) => ({
      id: `v-${page}-${i}`,
      type: 'visual',
      x: b.x / outputScale,
      y: b.y / outputScale,
      w: b.w / outputScale,
      h: b.h / outputScale,
    }));
    setVisualBboxesByPage((prev) => ({ ...prev, [page]: adjusted }));
  }

  function onCanvasReadyLeft(page, canvas) {
    leftCanvasesRef.current.set(page, canvas);
    maybeComputeVisualDiff(page);
  }
  function onCanvasReadyRight(page, canvas) {
    rightCanvasesRef.current.set(page, canvas);
    maybeComputeVisualDiff(page);
  }

  function onPageVisible(page) {
    if (requestedVisualPagesRef.current.has(page)) return;
    requestedVisualPagesRef.current.add(page);
    // Trigger immediately in case both canvases finished rendering before
    // the IntersectionObserver fired. If either is missing, the canvas-ready
    // callback will retry once it lands.
    maybeComputeVisualDiff(page);
  }

  // Keyboard shortcuts: J = next, K = prev, Esc = clear active highlight.
  useEffect(() => {
    function onKey(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'j' || e.key === 'J') { e.preventDefault(); gotoChange('next'); }
      else if (e.key === 'k' || e.key === 'K') { e.preventDefault(); gotoChange('prev'); }
      else if (e.key === 'Escape') { setActiveChangeId(null); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [changes, activeChangeId]);

  // When activeChangeId changes, scroll both panels to that page.
  useEffect(() => {
    if (!activeChangeId) return;
    const change = changes.find((c) => c.id === activeChangeId);
    if (!change) return;
    lastScrollSourceRef.current = null; // bypass feedback guard
    setScrollTargetLeft(change.page);
    setScrollTargetRight(change.page);
  }, [activeChangeId, changes]);

  function onLeftScroll(page) {
    if (!syncScroll) return;
    if (lastScrollSourceRef.current === 'right') {
      lastScrollSourceRef.current = null;
      return;
    }
    lastScrollSourceRef.current = 'left';
    setScrollTargetRight(page);
  }
  function onRightScroll(page) {
    if (!syncScroll) return;
    if (lastScrollSourceRef.current === 'left') {
      lastScrollSourceRef.current = null;
      return;
    }
    lastScrollSourceRef.current = 'right';
    setScrollTargetLeft(page);
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-2 bg-white border-b border-gray-200">
        <button
          onClick={onClose}
          className="p-2 rounded-md hover:bg-gray-100 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
          aria-label={t('common:buttons.close')}
        >
          <X size={18} />
        </button>
        <span className="text-sm font-medium text-gray-700 truncate">
          {file1.name} <span className="text-gray-400">vs</span> {file2.name}
        </span>

        <div className="flex-1" />

        <button
          onClick={() => gotoChange('prev')}
          aria-label={t('tools:compare.toolbar_prev', 'Previous change')}
          className="p-2 rounded hover:bg-gray-100"
        >
          <ChevronLeft size={18} />
        </button>
        <span className="text-sm text-gray-600 tabular-nums">
          {t('tools:compare.toolbar_changes_other', '{{count}} changes', { count: changes.length })}
        </span>
        <button
          onClick={() => gotoChange('next')}
          aria-label={t('tools:compare.toolbar_next', 'Next change')}
          className="p-2 rounded hover:bg-gray-100"
        >
          <ChevronRight size={18} />
        </button>

        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer ml-3">
          <input
            type="checkbox"
            checked={syncScroll}
            onChange={(e) => setSyncScroll(e.target.checked)}
            className="rounded"
          />
          {t('tools:compare.toolbar_sync_scroll', 'Sync scroll')}
        </label>

        <button
          onClick={() => setExportOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-blue text-white rounded-md text-sm font-medium hover:bg-brand-dark"
        >
          <Download size={14} />
          {t('tools:compare.toolbar_download', 'Download diff PDF')}
        </button>
      </div>

      {imageOnly && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 text-sm text-blue-800 flex items-center justify-between">
          <span>{t('tools:compare.image_only_banner', 'These PDFs appear to be scanned. Run OCR first for word-level comparison.')}</span>
          <a href="/ocr" className="text-blue-700 font-semibold hover:underline">{t('tools:compare.run_ocr_link', 'Run OCR →')}</a>
        </div>
      )}

      {/* Three-pane body */}
      <div className="flex-1 grid grid-cols-[1fr_240px_1fr] gap-2 p-2 overflow-hidden">
        <ComparePanel
          file={file1} side="left" label="v1"
          pageCount={Math.max(pageCount1, pageCount2)}
          onScrollPosition={onLeftScroll}
          scrollToPage={scrollTargetLeft}
          onPageVisible={onPageVisible}
          onCanvasReady={onCanvasReadyLeft}
          badges={visualBboxesByPage}
          activeChangeId={activeChangeId}
        />
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <ChangeList
            changes={changes}
            activeChangeId={activeChangeId}
            onSelect={setActiveChangeId}
            compareState={compareState}
            errorMessage={errorMessage}
          />
        </div>
        <ComparePanel
          file={file2} side="right" label="v2"
          pageCount={Math.max(pageCount1, pageCount2)}
          onScrollPosition={onRightScroll}
          scrollToPage={scrollTargetRight}
          onPageVisible={onPageVisible}
          onCanvasReady={onCanvasReadyRight}
          badges={visualBboxesByPage}
          activeChangeId={activeChangeId}
        />
      </div>

      {passwordPrompt && (
        <PdfPasswordModal
          open={true}
          title={t('tools:compare.password_modal_title', '{{side}} PDF is password-protected', {
            side: passwordPrompt.side === 'left' ? t('tools:compare.label_v1', 'Original') : t('tools:compare.label_v2', 'New version'),
          })}
          attemptsRemaining={passwordPrompt.attemptsRemaining}
          onSubmit={(pw) => {
            const side = passwordPrompt.side;
            passwordAttemptsRef.current[side] = (passwordAttemptsRef.current[side] || 0) + 1;
            session.providePassword(side, pw);
            setPasswordPrompt((p) => p ? { ...p, attemptsRemaining: p.attemptsRemaining - 1 } : null);
          }}
          onCancel={() => {
            setPasswordPrompt(null);
            onClose();
          }}
        />
      )}

      <CompareExportDialog
        open={exportOpen}
        defaultFilename={(file2.name.replace(/\.pdf$/i, '') + '-diff.pdf')}
        onCancel={() => setExportOpen(false)}
        onConfirm={handleExport}
      />
    </div>
  );
}

/**
 * Pad an RGBA `ImageData` to (W, H) so two differently-sized page canvases
 * can be compared by pixelmatch (which requires matching dimensions).
 * Fills the extra region with white — visually neutral, matches the PDF
 * page background pdf.js draws on transparent regions.
 */
function padRgba(imageData, W, H) {
  const { data, width: w, height: h } = imageData;
  if (w === W && h === H) return data;
  const out = new Uint8ClampedArray(W * H * 4).fill(255);
  for (let y = 0; y < h; y++) {
    const srcRow = y * w * 4;
    const dstRow = y * W * 4;
    out.set(data.subarray(srcRow, srcRow + w * 4), dstRow);
  }
  return out;
}
